/**
 * Report Dashboards
 *
 * Attio-style first-class dashboards: named containers that hold report
 * widgets in a grid layout, with per-user favorites, an auto-refresh interval,
 * and granular sharing (workspace / user / deal-collaborator access levels).
 */
import { z } from 'zod';
// =============================================================================
// Sharing
// =============================================================================
/**
 * Access ladder, ordered least- to most-permissive:
 *   no_access  - cannot see the dashboard or its reports
 *   read       - view the dashboard and its reports
 *   read_write - read + create/edit/delete reports on the dashboard
 *   full       - read_write + rename/delete the dashboard + manage its access
 */
export const REPORT_ACCESS_LEVELS = ['no_access', 'read', 'read_write', 'full'];
/** Comparable rank for each access level (higher = more permissive). */
export const ACCESS_RANK = {
    no_access: 0,
    read: 1,
    read_write: 2,
    full: 3,
};
/** True when `level` grants at least `required` access. */
export function accessAtLeast(level, required) {
    return ACCESS_RANK[level] >= ACCESS_RANK[required];
}
/** The more-permissive of two access levels. */
export function maxAccess(a, b) {
    return ACCESS_RANK[a] >= ACCESS_RANK[b] ? a : b;
}
/**
 * Share principals:
 *   org  - the whole workspace (principalId is null); the default level
 *   user - a specific member (principalId = userId)
 *   deal - a deal's collaborators (principalId = dealId); resolves to the
 *          accepted collaborators of that deal
 */
export const REPORT_PRINCIPAL_TYPES = ['org', 'user', 'deal'];
export const REPORT_RESOURCE_TYPES = ['report', 'dashboard'];
export const ReportShareSchema = z.object({
    id: z.string().uuid(),
    orgId: z.string().min(1),
    resourceType: z.enum(REPORT_RESOURCE_TYPES),
    resourceId: z.string().uuid(),
    principalType: z.enum(REPORT_PRINCIPAL_TYPES),
    principalId: z.string().nullable(),
    accessLevel: z.enum(REPORT_ACCESS_LEVELS),
    createdAt: z.string(),
});
// =============================================================================
// Dashboard
// =============================================================================
/** Period filter applied to a native dashboard widget. */
export const DASHBOARD_WIDGET_PERIODS = ['monthly', 'quarterly', 'yearly'];
export const DashboardWidgetSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('report'),
        id: z.string(),
        reportId: z.string(),
        x: z.number().int().min(0),
        y: z.number().int().min(0),
        w: z.number().int().positive(),
        h: z.number().int().positive(),
    }),
    z.object({
        kind: z.literal('native'),
        id: z.string(),
        nativeType: z.string(),
        period: z.enum(DASHBOARD_WIDGET_PERIODS).optional(),
        x: z.number().int().min(0),
        y: z.number().int().min(0),
        w: z.number().int().positive(),
        h: z.number().int().positive(),
    }),
]);
export const ReportDashboardSchema = z.object({
    id: z.string().uuid(),
    orgId: z.string().min(1),
    name: z.string().min(1),
    description: z.string().nullable(),
    createdBy: z.string(),
    autoRefreshMinutes: z.number().int().min(0),
    layout: z.array(DashboardWidgetSchema),
    createdAt: z.string(),
    updatedAt: z.string(),
    isFavorite: z.boolean().optional(),
    access: z.enum(REPORT_ACCESS_LEVELS).optional(),
});
// =============================================================================
// Default-dashboard templates
// =============================================================================
/** Which scope of org member a default-dashboard template targets. */
export const DASHBOARD_TEMPLATE_ROLES = ['manager', 'sales-rep'];
